import _ from 'lodash';

import { MHConferenceEvents } from '../base/lib-meet-hour';
import { PARTICIPANT_JOINED } from '../base/participants/actionTypes';
import { getLocalParticipant, getParticipantById, isLocalParticipantModerator } from '../base/participants/functions';
import MiddlewareRegistry from '../base/redux/MiddlewareRegistry';
import StateListenerRegistry from '../base/redux/StateListenerRegistry';
import { editMessage } from '../chat/actions.any';
import { MESSAGE_TYPE_REMOTE } from '../chat/constants';

import { UPDATE_BREAKOUT_ROOMS } from './actionTypes';
import { moveToRoom, sendParticipantToRoom } from './actions.any';
import { getBreakoutRooms } from './functions';
import logger from './logger';

/**
 * Registers a change handler for state['features/base/conference'].conference to
 * set the event listeners needed for the breakout rooms feature to operate.
 */
StateListenerRegistry.register(
    state => state['features/base/conference'].conference,
    (conference, { dispatch }, previousConference) => {
        if (conference && !previousConference) {
            conference.on(MHConferenceEvents.BREAKOUT_ROOMS_MOVE_TO_ROOM, roomId => {
                logger.debug(`Moving to room: ${roomId}`);
                dispatch(moveToRoom(roomId));
            });

            conference.on(MHConferenceEvents.BREAKOUT_ROOMS_UPDATED, ({ rooms, roomCounter }) => {
                logger.debug('Room list updated');
                if (typeof APP !== 'undefined') {
                    APP.API.notifyBreakoutRoomsUpdated(rooms);
                }
                dispatch({
                    type: UPDATE_BREAKOUT_ROOMS,
                    rooms,
                    roomCounter
                });
            });
        }
    });

// Track pending participant operations to prevent conflicts
const pendingParticipantOperations = new Set();

/**
 * State listener to handle participant state consistency during room switching
 */
StateListenerRegistry.register(
    state => state['features/base/mtApi']?.isSwitchingBreakOutRoom,
    (isSwitching, { getState }) => {
        if (isSwitching) {
            // Validate participant state during room switching
            const participantState = getState()['features/base/participants'];
            const localParticipant = getLocalParticipant(getState());

            if (!participantState || participantState.length === 0 || !localParticipant) {
                logger.warn('Invalid participant state detected during room switching');

                // Defer any UI updates that might cause crashes
                setTimeout(() => {
                    // Re-validate after a short delay
                    const newState = getState();
                    const newParticipantState = newState['features/base/participants'];
                    const newLocalParticipant = getLocalParticipant(newState);

                    if (!newParticipantState || !newLocalParticipant) {
                        logger.error('Participant state still invalid after delay');
                    }
                }, 100);
            }
        }
    }
);

MiddlewareRegistry.register(({ dispatch, getState }) => next => action => {
    const { type } = action;

    switch (type) {
    case UPDATE_BREAKOUT_ROOMS: {
        // Prevent concurrent updates that could cause state corruption
        const updateId = `${Date.now()}-${Math.random()}`;

        if (pendingParticipantOperations.size > 0) {
            logger.debug('Deferring breakout room update due to pending operations');

            // Defer this update to avoid conflicts
            setTimeout(() => {
                dispatch(action);
            }, 100);

            return next(action);
        }

        pendingParticipantOperations.add(updateId);

        try {
            // edit name if it was overwritten
            if (!action.updatedNames) {
                const { overwrittenNameList } = getState()['features/base/participants'];

                if (overwrittenNameList && Object.keys(overwrittenNameList).length > 0) {
                    const newRooms = {};

                    Object.entries(action.rooms).forEach(([ key, r ]) => {
                        let participants = r?.participants || {};
                        let jid;

                        for (const id of Object.keys(overwrittenNameList)) {
                            jid = Object.keys(participants).find(p => p.slice(p.indexOf('/') + 1) === id);

                            if (jid) {
                                participants = {
                                    ...participants,
                                    [jid]: {
                                        ...participants[jid],
                                        displayName: overwrittenNameList[id]
                                    }
                                };
                            }
                        }

                        newRooms[key] = {
                            ...r,
                            participants
                        };
                    });

                    action.rooms = newRooms;
                }
            }

            // update the chat history to match names for participants in breakout rooms
            const { messages } = getState()['features/chat'];

            messages?.forEach(m => {
                if (m.messageType === MESSAGE_TYPE_REMOTE && !getParticipantById(getState(), m.id)) {
                    const rooms = action.rooms;

                    for (const room of Object.values(rooms)) {
                        const participants = room.participants || {};
                        const matchedJid = Object.keys(participants).find(jid => jid.endsWith(m.id));

                        if (matchedJid) {
                            const newDisplayName = participants[matchedJid]?.displayName;

                            if (newDisplayName && newDisplayName !== m.displayName) {
                                dispatch(editMessage({
                                    ...m,
                                    displayName: newDisplayName,
                                    isEdited: Boolean(m.isEdited)
                                }));
                            }
                            break;
                        }
                    }
                }
            });

        } finally {
            // Always clean up the operation tracking
            pendingParticipantOperations.delete(updateId);
        }

        break;
    }
    }
    const result = next(action);

    if (type === PARTICIPANT_JOINED) {
        const state = getState();
        const { participant } = action;

        if (participant && !participant.local && isLocalParticipantModerator(state)) {
            const { persistentAutoAssign } = state['features/breakout-rooms'];

            if (persistentAutoAssign) {
                const rooms = getBreakoutRooms(state);
                const breakoutRooms = _.filter(rooms, room => !room.isMainRoom);
                const isAlreadyInBreakoutRoom = Object.values(rooms).some(room =>
                    !room.isMainRoom && room.participants && Object.keys(room.participants).some(jid => jid.endsWith(participant.id))
                );

                if (!isAlreadyInBreakoutRoom && breakoutRooms && breakoutRooms.length > 0) {
                    const randomRoom = breakoutRooms[Math.floor(Math.random() * breakoutRooms.length)];

                    logger.debug(`Persistent auto assign: sending participant ${participant.id} to room ${randomRoom.id}`);
                    dispatch(sendParticipantToRoom(participant.id, randomRoom.id));
                }
            }
        }
    }

    return result;
});
